/**
 * Recently viewed — per-browser only.
 *
 * The handle list lives in this browser's localStorage and nowhere else: no
 * cookie, no server call, nothing sent to Shopify or to us beyond the ordinary
 * search request that fetches the cards. Every storage access is wrapped,
 * because private windows and blocked site data make localStorage throw rather
 * than return null.
 *
 * The section stays hidden until at least one card renders, so a first-time
 * visitor never sees an empty shelf.
 */
(() => {
  const KEY = 'sarup:recently-viewed';
  const MAX = 20;

  const read = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {
      /* private window, blocked storage — the feature simply does nothing */
    }
  };

  // Record the current product, if this is a product page.
  const current = document.body.dataset.sarupProductHandle;
  if (current) {
    write([current, ...read().filter((h) => h !== current)]);
  }

  // Product pages show one row at most; the homepage keeps its own setting.
  const PRODUCT_PAGE_CAP = 4;

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      let limit = parseInt(this.dataset.limit, 10) || 4;
      if (current) limit = Math.min(limit, PRODUCT_PAGE_CAP);
      const handles = read().filter((h) => h !== current).slice(0, limit);
      if (!handles.length) return;

      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      const url = `${this.dataset.sectionUrl}?type=product&q=${encodeURIComponent(query)}&section_id=recently-viewed-results`;

      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const grid = doc.querySelector('.recently-viewed-results__grid');
          const target = this.querySelector('.recently-viewed__results');
          if (!grid || !target) return;
          // Search returns relevance order and may match more than asked:
          // keep only the listed handles, most recent first, capped.
          const items = new Map([...grid.querySelectorAll(':scope > li[data-handle]')].map((li) => [li.dataset.handle, li]));
          const ordered = handles.map((h) => items.get(h)).filter(Boolean).slice(0, limit);
          if (!ordered.length) return;
          grid.replaceChildren(...ordered);
          target.replaceChildren(grid);
          this.removeAttribute('hidden');
        })
        .catch(() => {
          /* leave the section hidden rather than showing an empty shelf */
        });
    }
  }

  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewed);
  }
})();
